import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import IntakeForm from "@/components/IntakeForm";
import api from "@/lib/api";
import { ArrowRight, Ear, Lightbulb, FileText, Share2, TrendingUp } from "lucide-react";

const PROCESS_STEPS = [
  {
    icon: Ear,
    title: "We Listen",
    body: "Every submission is reviewed and categorized. We remove personal information before using data for research.",
  },
  {
    icon: Lightbulb,
    title: "We Learn",
    body: "Experiences are combined with verified rental screening criteria to identify statewide trends. No single story determines a conclusion.",
  },
  {
    icon: FileText,
    title: "We Publish",
    body: "Findings are summarized in anonymous research reports available to the public. No individual stories or personal information are published without permission.",
  },
  {
    icon: Share2,
    title: "We Share",
    body: "Research findings are made available to policymakers, housing professionals, researchers, journalists, and the public.",
  },
  {
    icon: TrendingUp,
    title: "We Improve",
    body: "Our goal is to help improve understanding of California's rental market through transparent, evidence-based research.",
  },
];

const CA_MAP =
  "https://static.prod-images.emergentagent.com/jobs/091a4e65-0202-40d0-abed-2c5d0bbfcfb9/images/9940c20d819f7401708bb1e00e022bbd0261ef24a6f3021df14f24ce6925b448.png";

function CountUp({ value }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1200;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{n.toLocaleString()}</>;
}

export default function HomePage() {
  const { hash } = useLocation();
  const shareRef = useRef(null);
  const [count, setCount] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setCount(r.data.total)).catch(() => setCount(0));
  }, []);

  useEffect(() => {
    if (hash === "#share" && shareRef.current) {
      shareRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  const scrollToShare = () =>
    shareRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="min-h-screen bg-white font-body text-ink">
      <Header />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden border-b border-slate-200"
      >
        {/* faint California map */}
        <img
          src={CA_MAP}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-10 top-1/2 hidden h-[120%] -translate-y-1/2 select-none opacity-[0.16] md:block"
        />
        <div className="relative mx-auto max-w-7xl px-6 pb-20 pt-20 md:px-12 md:pb-28 md:pt-28">
          <div className="max-w-3xl crep-fade-up">
            <div className="mb-6 text-xs uppercase tracking-[0.22em] text-calblue">
              Independent · Nonpartisan · Research-focused
            </div>
            <h1 className="font-serif text-5xl font-medium leading-[1.04] tracking-tight text-ink md:text-7xl">
              Every Housing Experience Matters.
            </h1>
            <div className="mt-6">
              <div className="font-serif text-2xl font-normal text-slate-700 md:text-3xl">
                California Rental Experience Project
              </div>
              <div className="mt-1 text-xs uppercase tracking-[0.22em] text-calblue">
                Independent Housing Research Initiative
              </div>
            </div>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-slate-700">
              We collect anonymous rental experiences from renters, landlords, property
              managers, leasing professionals, and housing providers to better understand
              California's rental market.
            </p>
            <div className="mt-10 flex flex-col items-start gap-8 sm:flex-row sm:items-center">
              <button
                data-testid="hero-share-button"
                onClick={scrollToShare}
                className="inline-flex items-center rounded-none border border-navy px-8 py-4 text-base font-medium text-navy transition-colors hover:bg-navy hover:text-white focus:outline-none focus:ring-2 focus:ring-navy focus:ring-offset-2"
              >
                Add Your Voice <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <div data-testid="voices-counter" className="border-l border-slate-200 pl-8">
                <div className="font-mono text-4xl font-medium tracking-tighter text-ink">
                  {count === null ? "—" : <CountUp value={count} />}
                </div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">
                  Voices Heard
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUR PROMISE */}
      <section
        data-testid="promise-section"
        className="bg-navy px-6 py-24 text-white md:px-12 md:py-32"
      >
        <div className="mx-auto max-w-5xl">
          <div className="text-xs uppercase tracking-[0.25em] text-calblue">Our promise</div>
          <p className="mt-8 font-serif text-3xl font-normal leading-snug tracking-tight text-white sm:text-4xl md:text-5xl">
            We are not collecting stories to prove a point.
          </p>
          <p className="mt-4 font-serif text-3xl font-normal leading-snug tracking-tight text-white/80 sm:text-4xl md:text-5xl">
            We are collecting stories to understand the problem.
          </p>
        </div>
      </section>

      {/* WHY WE'RE LISTENING */}
      <section
        data-testid="why-section"
        className="border-y border-slate-200 bg-[#F8FAFC] px-6 py-24 md:px-12 md:py-32"
      >
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.2em] text-calblue">
                Why we're listening
              </div>
              <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">
                Every housing experience matters.
              </h2>
            </div>
            <div className="lg:col-span-8">
              <div className="grid gap-x-12 gap-y-8 sm:grid-cols-2">
                {[
                  "Some people found housing quickly.",
                  "Some struggled to find housing at all.",
                  "Some landlords had excellent tenants.",
                  "Some experienced significant financial loss.",
                ].map((t) => (
                  <div key={t} className="border-l-2 border-slate-300 pl-5">
                    <p className="text-lg leading-relaxed text-slate-700">{t}</p>
                  </div>
                ))}
              </div>
              <p className="mt-12 max-w-2xl text-lg leading-relaxed text-slate-700">
                Understanding both sides helps create better policy discussions. We are not
                here to assign blame. We are here to listen, document, and understand — so
                that everyone has a clearer, fairer picture of how renting works in
                California today.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT HAPPENS TO MY EXPERIENCE */}
      <section
        data-testid="process-section"
        className="px-6 py-24 md:px-12 md:py-32"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-calblue">
              Transparency
            </div>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">
              What happens to my experience?
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              A clear, five-step process — from the moment you share your experience to the
              research it helps inform.
            </p>
          </div>

          <div className="mt-16 grid gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-5">
            {PROCESS_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`process-step-${i + 1}`}
                  className="flex flex-col bg-white p-7"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-calblue">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <Icon className="h-5 w-5 text-navy" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl font-medium tracking-tight text-ink">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SHARE FORM */}
      <section
        id="share"
        ref={shareRef}
        data-testid="form-section"
        className="scroll-mt-24 px-6 py-24 md:px-12 md:py-32"
      >
        <div className="mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-calblue">
              Add your voice
            </div>
            <h2 className="mt-4 font-serif text-4xl font-medium tracking-tight text-ink md:text-5xl">
              A few questions. Your words.
            </h2>
            <p className="mx-auto mt-4 max-w-xl leading-relaxed text-slate-600">
              This takes just a few minutes. Your individual response stays private and is
              never published.
            </p>
          </div>
          <IntakeForm />
        </div>
      </section>

      {/* DATA TEASER */}
      <section className="border-t border-slate-200 bg-[#F8FAFC] px-6 py-20 md:px-12">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <div>
            <h2 className="font-serif text-3xl font-medium tracking-tight text-ink">
              What the data shows
            </h2>
            <p className="mt-2 max-w-xl text-slate-600">
              We publish only aggregate, anonymous statistics — never individual stories.
            </p>
          </div>
          <Link
            to="/data"
            data-testid="view-data-link"
            className="inline-flex items-center border border-ink px-6 py-3 text-sm font-medium text-ink transition-colors hover:bg-ink hover:text-white"
          >
            View the live data <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
